import appLogo from '@assets/images/icon-transparent.png';
import CommunityFollowButton from '@atoms/CommunityFollowButton';
import Colors from '@constants/Colors';
import {
  ArrowRight01Icon,
  ArrowUpRight01Icon,
  Calendar03Icon,
  Location01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import ProfileSiteButtons from '@molecules/ProfileSiteButtons';
import {
  useBulkGetCommunities,
  useGetCommunity,
  useGetEventLocation,
  useGetParentCommunityIds,
} from '@sunrise-ui/api-client';
import type { Event } from '@sunrise-ui/api/events';
import { EventTicketButton } from '@sunrise-ui/composites';
import {
  Loader,
  ProfileImg,
  TextBold,
  TextReg,
  formatDateRangeCondensed,
  formatSameDayTimeRange,
  openMapsLocation,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { router } from 'expo-router';
import React, { useCallback, useMemo } from 'react';
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native';

type Props = {
  event: Event;
  isLoading?: boolean;
  showPresentedBy?: boolean;
  // layout controls which parts of the header to render on desktop
  // 'full' (default) renders image + info; 'image' renders only image; 'info' renders only info
  layout?: 'full' | 'image' | 'info';
};

/**
 * EventLandingHeader - Hero section for event landing page
 *
 * Displays:
 * - Event image (from parent community's profile photo)
 * - Event name
 * - Date range (formatted nicely)
 * - Location with link to Google Maps
 * - "Get Tickets" CTA button
 * - "Presented By" community attribution
 */
export function EventLandingHeader({
  event,
  isLoading = false,
  showPresentedBy = true,
  layout = 'full',
}: Props) {
  const styles = useDynamicStyles(createStyles);

  // Fetch event's community data
  const { data: eventCommunity } = useGetCommunity(event?.community_id);

  // Fetch parent community IDs (the orgs that present this event)
  const { data: parentCommunityIds } = useGetParentCommunityIds({
    communityId: event?.community_id,
  });

  // Fetch parent community (presenter org) - use first one for display
  const { data: parentCommunities } = useBulkGetCommunities({
    communityIds: parentCommunityIds,
    enabled: Boolean(parentCommunityIds?.length),
  });

  // Use parent community if available, otherwise fall back to event community
  const community = useMemo(() => {
    if (parentCommunities && parentCommunities.length > 0) {
      return parentCommunities[0];
    }
    return eventCommunity;
  }, [parentCommunities, eventCommunity]);

  // Fetch location details
  const { data: location } = useGetEventLocation(event?.event_location_id);

  const formattedDate = formatDateRangeCondensed(
    event?.start_date,
    event?.scheduled_end_date,
  );

  const formattedTime = formatSameDayTimeRange(
    event?.start_date,
    event?.scheduled_end_date,
  );

  // Get event image - prioritize event community's own image, fall back to parent community's image
  const eventImageUrl = useMemo(() => {
    // First try event community's photos
    const eventPhoto =
      eventCommunity?.photos?.large ??
      eventCommunity?.photos?.medium ??
      eventCommunity?.photos?.small ??
      eventCommunity?.photos?.thumbnail_large ??
      eventCommunity?.photos?.thumbnail_small;

    if (eventPhoto) {
      return eventPhoto;
    }

    // Fall back to parent community's photos
    const parentCommunity = parentCommunities?.[0];
    return (
      parentCommunity?.photos?.large ??
      parentCommunity?.photos?.medium ??
      parentCommunity?.photos?.small ??
      parentCommunity?.photos?.thumbnail_large ??
      parentCommunity?.photos?.thumbnail_small
    );
  }, [eventCommunity?.photos, parentCommunities]);

  // Build display location text
  const displayLocation = useMemo(() => {
    if (!location) return '';
    const parts = [location.city, location.state].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : location.name;
  }, [location]);

  const handleLocationPress = useCallback(() => {
    if (!location) return;
    openMapsLocation({
      name: location.name,
      address: location.address,
      city: location.city,
      state: location.state,
      postalCode: location.postal_code,
    });
  }, [location]);

  const handleCommunityPress = useCallback(() => {
    if (!community?.slug) return;
    router.navigate(`/communities/${community.slug}`);
  }, [community?.slug]);

  if (isLoading || !event) {
    return (
      <View style={styles.container}>
        <Loader />
      </View>
    );
  }

  // Render image only (for desktop left column)
  if (layout === 'image') {
    return (
      <View style={styles.imageSection}>
        {eventImageUrl ? (
          <Image
            source={{ uri: eventImageUrl }}
            style={styles.eventImage}
            resizeMode="cover"
            accessibilityLabel={`${event.name} event image`}
          />
        ) : (
          <View style={[styles.eventImage, styles.eventImagePlaceholder]}>
            <Image
              source={appLogo}
              style={styles.placeholderLogo}
              resizeMode="contain"
            />
          </View>
        )}
      </View>
    );
  }

  // Render info only (for desktop right column)
  if (layout === 'info') {
    return (
      <View style={styles.infoSection}>
        <TextBold style={styles.eventName}>{event.name}</TextBold>

        {/* Meta blocks with bordered treatment */}
        <View style={styles.metaBlocksContainer}>
          {formattedDate && (
            <View style={styles.metaBlock}>
              <View style={styles.iconContainer}>
                <HugeiconsIcon
                  icon={Calendar03Icon}
                  size={20}
                  color={Colors.dark.opacity50}
                />
              </View>
              <View style={styles.infoTextContainer}>
                <TextReg style={styles.infoText}>{formattedDate}</TextReg>
                {formattedTime && (
                  <TextReg style={styles.infoTextSecondary}>
                    {formattedTime}
                  </TextReg>
                )}
              </View>
            </View>
          )}

          {location && (
            <TouchableOpacity
              style={styles.metaBlock}
              onPress={handleLocationPress}
              activeOpacity={0.7}
            >
              <View style={styles.iconContainer}>
                <HugeiconsIcon
                  icon={Location01Icon}
                  size={20}
                  color={Colors.dark.opacity50}
                />
              </View>
              <View style={styles.infoTextContainer}>
                <View style={styles.locationNameRow}>
                  <TextReg style={[styles.infoText, styles.linkText]}>
                    {location.name}
                  </TextReg>
                  <HugeiconsIcon
                    icon={ArrowUpRight01Icon}
                    size={14}
                    color={Colors.dark.text}
                  />
                </View>
                {displayLocation !== location.name && (
                  <TextReg style={styles.infoTextSecondary}>
                    {displayLocation}
                  </TextReg>
                )}
              </View>
            </TouchableOpacity>
          )}
        </View>

        {event.id && (
          <View style={styles.ctaContainer}>
            <EventTicketButton eventId={event.id} size="medium" />
          </View>
        )}
      </View>
    );
  }

  // Full layout (for mobile - stacked)
  return (
    <View style={styles.container}>
      <View style={styles.imageSection}>
        {eventImageUrl ? (
          <Image
            source={{ uri: eventImageUrl }}
            style={styles.eventImage}
            resizeMode="cover"
            accessibilityLabel={`${event.name} event image`}
          />
        ) : (
          <View style={[styles.eventImage, styles.eventImagePlaceholder]}>
            <Image
              source={appLogo}
              style={styles.placeholderLogo}
              resizeMode="contain"
            />
          </View>
        )}
      </View>

      <View style={styles.infoSection}>
        <TextBold style={styles.eventName}>{event.name}</TextBold>
        {formattedDate && (
          <View style={styles.infoRow}>
            <View style={styles.iconContainer}>
              <HugeiconsIcon
                icon={Calendar03Icon}
                size={18}
                color={Colors.dark.opacity50}
              />
            </View>
            <View style={styles.infoTextContainer}>
              <TextReg style={styles.infoText}>{formattedDate}</TextReg>
              {formattedTime && (
                <TextReg style={styles.infoTextSecondary}>
                  {formattedTime}
                </TextReg>
              )}
            </View>
          </View>
        )}

        {location && (
          <TouchableOpacity
            style={styles.infoRow}
            onPress={handleLocationPress}
            activeOpacity={0.7}
          >
            <View style={styles.iconContainer}>
              <HugeiconsIcon
                icon={Location01Icon}
                size={18}
                color={Colors.dark.opacity50}
              />
            </View>
            <View style={styles.infoTextContainer}>
              <View style={styles.locationNameRow}>
                <TextReg style={[styles.infoText, styles.linkText]}>
                  {location.name}
                </TextReg>
                <HugeiconsIcon
                  icon={ArrowUpRight01Icon}
                  size={14}
                  color={Colors.dark.text}
                />
              </View>
              {displayLocation !== location.name && (
                <TextReg style={styles.infoTextSecondary}>
                  {displayLocation}
                </TextReg>
              )}
            </View>
          </TouchableOpacity>
        )}

        {event.id && (
          <View style={styles.ctaContainer}>
            <EventTicketButton eventId={event.id} size="medium" />
          </View>
        )}
      </View>

      {showPresentedBy && community && (
        <View style={styles.presentedBySection}>
          <TextReg style={styles.presentedByLabel}>Presented by</TextReg>
          <View style={styles.presentedByRow}>
            <TouchableOpacity
              style={styles.communityCard}
              onPress={handleCommunityPress}
              activeOpacity={0.7}
            >
              <ProfileImg
                userId={community.id ?? ''}
                imgSize="thumbnail_large"
                styleSize={40}
                profile={{
                  first_name: community.name,
                  last_name: '',
                  photos: community.photos,
                }}
              />
              <View style={styles.communityInfo}>
                <View style={styles.communityNameRow}>
                  <TextBold style={styles.communityName}>
                    {community.name}
                  </TextBold>
                  <HugeiconsIcon
                    icon={ArrowRight01Icon}
                    size={16}
                    color={Colors.dark.text}
                  />
                </View>
              </View>
            </TouchableOpacity>
            <CommunityFollowButton communityId={community.id} />
          </View>

          {/* Community Headline */}
          {community.headline && (
            <TextReg style={styles.communityHeadline} numberOfLines={3}>
              {community.headline}
            </TextReg>
          )}

          {/* Community Social Links */}
          {(community.links?.linkedin ||
            community.links?.github ||
            community.links?.x ||
            community.links?.website) && (
            <ProfileSiteButtons
              siteLinks={{
                linkedin: community.links?.linkedin,
                github: community.links?.github,
                x: community.links?.x,
                website: community.links?.website,
              }}
            />
          )}
        </View>
      )}
    </View>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      width: '100%',
      alignItems: 'flex-start',
      paddingHorizontal: isMobile ? 16 : 24,
      paddingTop: isMobile ? 16 : 24,
      gap: isMobile ? 20 : 24,
    },
    imageSection: {
      alignItems: isMobile ? 'center' : 'flex-start',
      width: isMobile ? '100%' : undefined,
    },
    eventImage: {
      width: isMobile ? 300 : 320,
      height: isMobile ? 300 : 320,
      borderRadius: 12,
      maxWidth: '100%',
    },
    eventImagePlaceholder: {
      backgroundColor: 'transparent',
      borderWidth: 2,
      borderColor: Colors.dark.opacity50,
      justifyContent: 'center',
      alignItems: 'center',
    },
    placeholderLogo: {
      width: isMobile ? 80 : 100,
      height: isMobile ? 80 : 100,
      opacity: 0.6,
    },
    infoSection: {
      width: '100%',
      maxWidth: isMobile ? 600 : undefined,
      alignItems: 'flex-start',
      gap: 16,
    },
    eventName: {
      fontSize: isMobile ? 24 : 32,
      textAlign: 'left',
      color: Colors.dark.text,
    },
    metaBlocksContainer: {
      gap: 16,
      width: '100%',
    },
    metaBlock: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
    },
    iconContainer: {
      padding: 10,
      borderWidth: 1,
      borderColor: Colors.dark.opacity20,
      borderRadius: 8,
    },
    infoRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
      width: '100%',
      justifyContent: 'flex-start',
    },
    infoTextContainer: {
      flex: 1,
      alignItems: 'flex-start',
      justifyContent: 'center',
    },
    locationNameRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    infoText: {
      fontSize: isMobile ? 15 : 16,
      color: Colors.dark.text,
    },
    infoTextSecondary: {
      fontSize: isMobile ? 13 : 14,
      color: Colors.dark.text,
      opacity: 0.7,
    },
    linkText: {
      textDecorationLine: 'underline',
    },
    ctaContainer: {
      marginTop: isMobile ? 8 : 12,
      alignSelf: 'flex-start',
    },
    presentedBySection: {
      width: '100%',
      alignItems: 'flex-start',
      gap: 8,
      paddingTop: isMobile ? 8 : 12,
    },
    presentedByRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      width: '100%',
    },
    presentedByLabel: {
      fontSize: 12,
      color: Colors.dark.text,
      opacity: 0.7,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    communityCard: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      flex: 1,
    },
    communityInfo: {
      flex: 1,
    },
    communityNameRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    communityName: {
      fontSize: 15,
      color: Colors.dark.text,
    },
    communityHeadline: {
      fontSize: 14,
      color: Colors.dark.text,
      opacity: 0.8,
      lineHeight: 20,
    },
  });
